/**
 * سربرگِ «پرچم‌های رخداد» — همه‌ی پرچم‌هایی که کاربرها روی رخدادها زده‌اند
 * (مایگریشن 0200).
 *
 * ═══ چرا وجود دارد ═══
 *
 * از 0115 هر کاربر روی هر رخداد پرچم می‌زند — «تگِ اشتباه»، «قیمت جا افتاده»،
 * «تکراری»، … — ولی تا این سربرگ هیچ‌کس جز خودِ آن کاربر آن را نمی‌دید.
 *
 * ═══ سه کار، یک صفحه ═══
 *
 *   • **فهرست** — تک‌تکِ پرچم‌ها (یا گروه‌شده به ازای رخداد)، با فیلتر و
 *     تایید/ردِ تکی و گروهی.
 *   • **اصلاح** — کلیک روی رخداد پنلِ `FixPanel` را باز می‌کند: پرچم‌های همان
 *     رخداد و فرمِ اصلاحش. پرچم‌ها بعد از اصلاح **می‌مانند**.
 *   • **آمار** — روی همان سطرهایی که فیلتر شده‌اند؛ پس «فقط تگ‌ها» آمار را هم
 *     فقط تگ‌ها می‌کند.
 *
 * فرق با «گزارش‌ها»: آن‌جا گزارشِ **آدم** درباره‌ی آدمِ دیگر است (سیگنال، پیام،
 * پروفایل). این‌جا حرفِ کاربر درباره‌ی **داده‌ی رخداد** است — چیزی که کرالر
 * آورده و باید درست شود.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { opsRepo } from '@/core/data/opsRepo';
import { tagRepo } from '@/core/data/miscRepo';
import { formatDateTime } from '@/core/lib/dates';
import { reportError } from '@/core/lib/report';
import type { IssueKind } from '@/core/lib/issues';
import {
  FIELD_LABEL, KIND_ICON, KIND_LABEL, STATUS_LABEL,
  buildIssueStats, filterIssues, groupByEvent, isActionable, issueStatus, parseEventIssue, rowKey,
  type EventIssueRow, type EventIssuesData, type IssueFilter, type IssueStatus,
} from '@/core/lib/eventIssuesReport';
import { Hint } from '@/ui/Hint';
import { fmtNum as fmt } from '@/ui/Num';
import { DayColumns, Legend, ShareBar, StackBars } from './Charts';
import { FixPanel } from './FixPanel';

const PAGE = 100;
const STATUSES: (IssueStatus | 'all')[] = ['open', 'accepted', 'rejected', 'all'];
const KINDS: IssueKind[] = ['wrong', 'missed', 'duplicate', 'inappropriate', 'other'];

const pct = (v: number | null) => (v == null ? '—' : `${Math.round(v * 100)}٪`);
const hours = (h: number | null) =>
  h == null ? '—' : h < 1 ? `${Math.round(h * 60)} دقیقه` : h < 48 ? `${h.toFixed(1)} ساعت` : `${(h / 24).toFixed(1)} روز`;

/** خواندنِ `?tab=issues&status=…` تا لینکِ یک فیلتر قابلِ فرستادن باشد. */
const param = (k: string): string | null => {
  try { return new URLSearchParams(window.location.search).get(k); } catch { return null; }
};

export function EventIssuesTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['eventIssues'], queryFn: () => opsRepo.eventIssues() });
  const tags = useQuery({ queryKey: ['tags', 'withArchived'], queryFn: tagRepo.listWithArchived });

  const [view, setView] = useState<'list' | 'events' | 'stats'>(
    (param('view') as 'list' | 'events' | 'stats' | null) ?? 'list');
  const [f, setF] = useState<IssueFilter>({
    status: (param('status') as IssueStatus | 'all' | null) ?? 'open',
    kind: 'all', field: 'all', q: '', hideInternal: false, hideWithdrawn: false,
  });
  const [days, setDays] = useState(30);
  const [shown, setShown] = useState(PAGE);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [note, setNote] = useState('');
  const [openEvent, setOpenEvent] = useState<string | null>(null);

  const review = useMutation({
    mutationFn: (a: { ids: number[]; verdict: 'accepted' | 'rejected' | 'open' }) =>
      opsRepo.reviewEventIssues(a.ids, a.verdict, note),
    onSuccess: () => { setPicked(new Set()); qc.invalidateQueries({ queryKey: ['eventIssues'] }); },
    onError: (err) => reportError('admin.eventIssues.review', err),
  });

  const data: EventIssuesData = q.data ?? { issues: [], events: {} };
  // فیلترِ وضعیت روی شمارنده‌های خودِ قرص‌ها اثر نمی‌گذارد — وگرنه هر قرص جز
  // قرصِ انتخاب‌شده صفر نشان می‌داد.
  const base = useMemo(() => filterIssues(data, { ...f, status: 'all' }), [data, f]);
  const rows = useMemo(() => filterIssues(data, f), [data, f]);
  const stats = useMemo(() => buildIssueStats(data, rows, { days }), [data, rows, days]);
  const groups = useMemo(() => groupByEvent(rows), [rows]);

  const setFilter = (patch: Partial<IssueFilter>) => { setF((x) => ({ ...x, ...patch })); setShown(PAGE); setPicked(new Set()); };
  const count = (s: IssueStatus | 'all') => (s === 'all' ? base.length : base.filter((r) => issueStatus(r) === s).length);
  const togglePick = (id: number) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const visible = rows.slice(0, shown);
  const visibleIds = visible.filter(isActionable).map((r) => r.issue_id!);
  const allPicked = visibleIds.length > 0 && visibleIds.every((id) => picked.has(id));

  if (q.isLoading) return <p className="dim">…</p>;
  if (q.error) {
    return (
      <div className="card">
        <p className="err">{String((q.error as Error).message ?? q.error)}</p>
        <p className="dim">اگر خطا «function … does not exist» است، مایگریشنِ 0200 روی این دیتابیس اجرا نشده.</p>
      </div>
    );
  }

  const fieldsSeen = [...new Set(base.map((r) => parseEventIssue(r.text).field).filter(Boolean))] as string[];
  const opened = openEvent ? data.events[openEvent] : null;

  return (
    <>
      <div className="grid n4">
        <div className="card stat warn">
          <div className="n">{fmt(stats.totals.open)}</div>
          <div className="k"><Hint text="باز" help="پرچم‌هایی که هنوز تصمیمی رویشان نیست (با فیلترهای فعلی)" /></div>
        </div>
        <div className="card stat good">
          <div className="n">{fmt(stats.totals.accepted)}</div>
          <div className="k"><Hint text={`تاییدشده · ${fmt(stats.totals.fixed)} اصلاح‌شده`}
            help="حرفِ کاربر درست بود. «اصلاح‌شده» یعنی رخداد از همین سربرگ اصلاح شد و پرچم به آن وصل ماند" /></div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(stats.totals.rejected)}</div>
          <div className="k">ردشده</div>
        </div>
        <div className="card stat">
          <div className="n">{pct(stats.acceptRate)}</div>
          <div className="k"><Hint text="نرخِ درستی" help="تاییدشده ÷ (تاییدشده + ردشده). پرچمِ باز در آن نیست" /></div>
        </div>
      </div>

      <div className="card">
        <div className="chiprow">
          {(['list', 'events', 'stats'] as const).map((v) => (
            <button key={v} type="button" className={`tab${view === v ? ' on' : ''}`} onClick={() => setView(v)}>
              {v === 'list' ? 'تک‌تکِ پرچم‌ها' : v === 'events' ? 'به ازای رخداد' : 'آمار و نمودار'}
            </button>
          ))}
        </div>

        <div className="chiprow" style={{ marginTop: 10 }}>
          {STATUSES.map((s) => (
            <button key={s} type="button" className={`chip${f.status === s ? ' on' : ''}`}
              onClick={() => setFilter({ status: s })}>
              {s === 'all' ? 'همه' : STATUS_LABEL[s]} {fmt(count(s))}
            </button>
          ))}
          <select className="input" style={{ width: 'auto' }} value={f.kind}
            onChange={(e) => setFilter({ kind: e.target.value as IssueKind | 'all' })}>
            <option value="all">همه‌ی دسته‌ها</option>
            {KINDS.map((k) => <option key={k} value={k}>{KIND_ICON[k]} {KIND_LABEL[k]}</option>)}
          </select>
          <select className="input" style={{ width: 'auto' }} value={f.field}
            onChange={(e) => setFilter({ field: e.target.value })}>
            <option value="all">همه‌ی فیلدها</option>
            {Object.keys(FIELD_LABEL).concat(fieldsSeen.filter((x) => !(x in FIELD_LABEL))).map((k) => (
              <option key={k} value={k}>{FIELD_LABEL[k] ?? k}</option>
            ))}
          </select>
          <input className="input" style={{ width: 220 }} placeholder="جستجو: متن، رخداد، #شناسه، کاربر، منبع"
            value={f.q} onChange={(e) => setFilter({ q: e.target.value })} />
          <label><input type="checkbox" checked={!!f.hideInternal}
            onChange={(e) => setFilter({ hideInternal: e.target.checked })} /> بی حساب‌های تستی</label>
          <label><input type="checkbox" checked={!!f.hideWithdrawn}
            onChange={(e) => setFilter({ hideWithdrawn: e.target.checked })} /> بی پس‌گرفته‌ها</label>
        </div>

        {data.issues.length === 0 ? (
          <p className="dim" style={{ marginTop: 10 }}>
            هنوز هیچ پرچمی نیامده. کاربر از دکمه‌ی پرچم ⚑ روی کارت یا صفحه‌ی جزئیاتِ رخداد می‌زند (هم در اپ، هم در سایت).
          </p>
        ) : null}

        {view === 'list' && data.issues.length > 0 ? (
          <>
            {picked.size > 0 ? (
              <div className="iss-bulk">
                <b>{fmt(picked.size)} انتخاب‌شده</b>
                <input className="input" style={{ width: 240 }} placeholder="یادداشت (اختیاری)"
                  value={note} onChange={(e) => setNote(e.target.value)} />
                <button type="button" className="btn" disabled={review.isPending}
                  onClick={() => review.mutate({ ids: [...picked], verdict: 'accepted' })}>تایید</button>
                <button type="button" className="btn ghost" disabled={review.isPending}
                  onClick={() => review.mutate({ ids: [...picked], verdict: 'rejected' })}>رد</button>
                <button type="button" className="btn ghost" disabled={review.isPending}
                  onClick={() => review.mutate({ ids: [...picked], verdict: 'open' })}>دوباره باز</button>
                <button type="button" className="linkbtn" onClick={() => setPicked(new Set())}>لغو</button>
              </div>
            ) : null}
            {review.error ? <p className="err">{String((review.error as Error).message ?? review.error)}</p> : null}
            {rows.length === 0 ? <p className="dim" style={{ marginTop: 10 }}>با این فیلترها چیزی نیست.</p> : (
              <div className="scrollx" style={{ marginTop: 8 }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>
                        <input type="checkbox" checked={allPicked} title="همه‌ی پرچم‌های این صفحه"
                          onChange={() => setPicked(allPicked ? new Set() : new Set(visibleIds))} />
                      </th>
                      <th>پرچم</th><th>رخداد</th><th>گزارش‌دهنده</th><th>کِی</th><th>وضعیت</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r) => (
                      <IssueRow key={rowKey(r)} r={r} data={data} picked={r.issue_id != null && picked.has(r.issue_id)}
                        busy={review.isPending}
                        onPick={() => r.issue_id != null && togglePick(r.issue_id)}
                        onOpen={() => setOpenEvent(r.event_id)}
                        onReview={(verdict) => review.mutate({ ids: [r.issue_id!], verdict })} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {rows.length > shown ? (
              <button type="button" className="btn ghost" style={{ marginTop: 8 }}
                onClick={() => setShown((n) => n + PAGE)}>
                {fmt(Math.min(PAGE, rows.length - shown))} تای دیگر ({fmt(rows.length - shown)} مانده)
              </button>
            ) : null}
          </>
        ) : null}

        {view === 'events' && data.issues.length > 0 ? (
          groups.length === 0 ? <p className="dim" style={{ marginTop: 10 }}>با این فیلترها چیزی نیست.</p> : (
            <div className="scrollx" style={{ marginTop: 8 }}>
              <table className="tbl">
                <thead><tr><th>رخداد</th><th>منبع</th><th>شهر</th><th className="num">باز</th><th className="num">همه</th><th>فیلدها</th><th>آخرین پرچم</th></tr></thead>
                <tbody>
                  {groups.slice(0, shown).map((g) => {
                    const ev = data.events[g.eventId];
                    const fields = [...new Set(g.rows.map((r) => {
                      const p = parseEventIssue(r.text);
                      return p.field ? FIELD_LABEL[p.field] ?? p.field : KIND_LABEL[p.kind];
                    }))];
                    return (
                      <tr key={g.eventId}>
                        <td>
                          <button type="button" className="linkbtn" onClick={() => setOpenEvent(g.eventId)}>
                            #{ev?.short_id ?? '?'} {ev?.title ?? g.eventId}
                          </button>
                        </td>
                        <td className="mono dim">{ev?.source ?? '—'}</td>
                        <td className="dim">{ev?.city ?? '—'}</td>
                        <td className={`num${g.open ? ' bad' : ''}`}>{fmt(g.open)}</td>
                        <td className="num">{fmt(g.rows.length)}</td>
                        <td className="dim">{fields.join('، ')}</td>
                        <td className="dim">{formatDateTime(g.latest)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {groups.length > shown ? (
                <button type="button" className="btn ghost" style={{ marginTop: 8 }}
                  onClick={() => setShown((n) => n + PAGE)}>بیشتر ({fmt(groups.length - shown)} مانده)</button>
              ) : null}
            </div>
          )
        ) : null}
      </div>

      {view === 'stats' && data.issues.length > 0 ? (
        <Stats stats={stats} days={days} setDays={setDays}
          onField={(k) => { setFilter({ field: k }); setView('list'); }}
          onKind={(k) => { setFilter({ kind: k as IssueKind }); setView('list'); }}
          onEvent={(id) => setOpenEvent(id)}
          onSearch={(s) => { setFilter({ q: s }); setView('list'); }} />
      ) : null}

      {opened ? (
        <FixPanel key={opened.id} event={opened} tags={tags.data ?? []}
          rows={data.issues.filter((r) => r.event_id === opened.id)}
          onClose={() => setOpenEvent(null)} />
      ) : openEvent ? (
        // رخداد در جواب نیست — یعنی پاک شده (on delete cascade) یا فهرست بریده شد.
        <p className="err">این رخداد دیگر در فهرست نیست.</p>
      ) : null}
    </>
  );
}

function IssueRow({ r, data, picked, busy, onPick, onOpen, onReview }: {
  r: EventIssueRow;
  data: EventIssuesData;
  picked: boolean;
  busy: boolean;
  onPick: () => void;
  onOpen: () => void;
  onReview: (v: 'accepted' | 'rejected' | 'open') => void;
}) {
  const p = parseEventIssue(r.text);
  const st = issueStatus(r);
  const ev = data.events[r.event_id];
  const live = isActionable(r);
  return (
    <tr>
      <td><input type="checkbox" disabled={!live} checked={picked} onChange={onPick} /></td>
      <td>
        <span title={r.text}>{KIND_ICON[p.kind]} {p.summary}</span>
        {r.note ? <div className="dim" style={{ fontSize: 12 }}>یادداشت: {r.note}</div> : null}
      </td>
      <td>
        <button type="button" className="linkbtn" onClick={onOpen} title="پرچم‌های این رخداد و اصلاحش">
          #{ev?.short_id ?? '?'} {ev?.title ?? '—'}
        </button>
        <div className="dim mono" style={{ fontSize: 11 }}>{[ev?.source, ev?.city].filter(Boolean).join(' · ')}</div>
      </td>
      <td className="dim">
        {r.who ?? 'بی‌نام'}{r.username ? ` @${r.username}` : ''}
        {r.internal ? <span className="chip" style={{ marginInlineStart: 4 }}>تستی</span> : null}
      </td>
      <td className="dim">{formatDateTime(r.at)}</td>
      <td>
        <span className={`chip iss-${st}`}>{STATUS_LABEL[st]}</span>
        {r.fixed_at ? <span className="chip on" title={`${formatDateTime(r.fixed_at)} · ${r.fixed_fields.join(', ')}`}>اصلاح شد</span> : null}
        {r.withdrawn ? <span className="chip" title="کاربر پرچمش را برداشت؛ تصمیمِ تو مانده">پس گرفت</span> : null}
        {r.decided_by ? <div className="dim" style={{ fontSize: 11 }}>{r.decided_by}{r.decided_at ? ` · ${formatDateTime(r.decided_at)}` : ''}</div> : null}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {live ? (
          <>
            {st !== 'accepted' ? <button type="button" className="linkbtn" disabled={busy} onClick={() => onReview('accepted')}>تایید</button> : null}
            {st === 'open' ? ' · ' : null}
            {st !== 'rejected' ? <button type="button" className="linkbtn" disabled={busy} onClick={() => onReview('rejected')}>رد</button> : null}
            {st !== 'open' ? <>{' · '}<button type="button" className="linkbtn" disabled={busy} onClick={() => onReview('open')}>باز کن</button></> : null}
            {' · '}
          </>
        ) : null}
        <button type="button" className="linkbtn" onClick={onOpen}>اصلاح</button>
      </td>
    </tr>
  );
}

function Stats({ stats, days, setDays, onField, onKind, onEvent, onSearch }: {
  stats: ReturnType<typeof buildIssueStats>;
  days: number;
  setDays: (n: number) => void;
  onField: (k: string) => void;
  onKind: (k: string) => void;
  onEvent: (id: string) => void;
  onSearch: (s: string) => void;
}) {
  const t = stats.totals;
  return (
    <>
      <div className="grid n4">
        <div className="card stat">
          <div className="n">{fmt(t.total)}</div>
          <div className="k">پرچم روی {fmt(t.events)} رخداد، از {fmt(t.reporters)} نفر</div>
        </div>
        <div className="card stat">
          <div className="n">{hours(stats.decideHours.median)}</div>
          <div className="k"><Hint text={`میانه‌ی زمانِ تصمیم · p90 ${hours(stats.decideHours.p90)}`}
            help={`از ثبتِ پرچم تا اولین تصمیمِ ادمین، روی ${stats.decideHours.n} پرچمِ تصمیم‌گرفته`} /></div>
        </div>
        <div className={`card stat${(stats.oldestOpenDays ?? 0) > 7 ? ' warn' : ''}`}>
          <div className="n">{stats.oldestOpenDays == null ? '—' : `${Math.floor(stats.oldestOpenDays)} روز`}</div>
          <div className="k">کهنه‌ترین پرچمِ باز</div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(t.internal)} · {fmt(t.withdrawn)}</div>
          <div className="k"><Hint text="از حساب‌های تستی · پس‌گرفته"
            help="حساب‌های داخلی/تستی از 0119 (سربرگِ «کاربران»). پس‌گرفته = کاربر پرچمش را برداشت ولی تصمیمِ ادمین مانده" /></div>
        </div>
      </div>

      <div className="card">
        <h2>سهمِ وضعیت‌ها</h2>
        <ShareBar open={t.open} accepted={t.accepted} rejected={t.rejected} />
        <Legend />
      </div>

      <div className="card">
        <div className="chiprow">
          <h2 style={{ margin: 0 }}>پرچم در روز</h2>
          <span className="spacer" style={{ flex: 1 }} />
          {[14, 30, 90].map((n) => (
            <button key={n} type="button" className={`chip${days === n ? ' on' : ''}`} onClick={() => setDays(n)}>{n} روز</button>
          ))}
        </div>
        <DayColumns rows={stats.byDay} />
        <Legend />
      </div>

      <div className="grid n2">
        <div className="card">
          <h2><Hint text="به ازای دسته" help="ستونِ آخر نرخِ درستی است. کلیک = فیلترِ فهرست" /></h2>
          <StackBars rows={stats.byKind} onPick={onKind} />
        </div>
        <div className="card">
          <h2><Hint text="به ازای فیلد" help="فقط «اشتباه» و «جاافتاده» فیلد دارند. کلیک = فیلترِ فهرست" /></h2>
          <StackBars rows={stats.byField} onPick={onField} empty="پرچمِ فیلدداری نیست." />
        </div>
        <div className="card">
          <h2><Hint text="تگ‌هایی که بیشتر گفته شده‌اند" help="✏️ = گفته شده اشتباه است، ➕ = گفته شده جا افتاده" /></h2>
          <StackBars rows={stats.byTag} empty="پرچمِ تگی نیست." onPick={(k) => onSearch(k.split(':').slice(1).join(':'))} />
        </div>
        <div className="card">
          <h2><Hint text="به ازای منبعِ کرالر" help="منبعی که پرچمِ تاییدشده‌ی زیادی دارد، جای اصلاحِ کرالر است، نه یکی‌یکی در این‌جا" /></h2>
          <StackBars rows={stats.bySource} onPick={(k) => onSearch(k)} />
        </div>
        <div className="card">
          <h2>به ازای شهر</h2>
          <StackBars rows={stats.byCity} onPick={(k) => onSearch(k)} />
        </div>
        <div className="card">
          <h2>پرچم‌خورده‌ترین رخدادها</h2>
          <StackBars rows={stats.topEvents} onPick={onEvent} />
        </div>
        <div className="card">
          <h2><Hint text="فعال‌ترین گزارش‌دهنده‌ها" help="نرخِ درستیِ هر نفر کنارش: کسی که همیشه رد می‌شود، شاید منو را بد فهمیده" /></h2>
          <StackBars rows={stats.topReporters} />
        </div>
        <div className="card">
          <h2>ستون‌هایی که از این‌جا اصلاح شدند</h2>
          {stats.fixedFields.length === 0 ? <p className="dim">هنوز هیچ اصلاحی از این سربرگ نشده.</p> : (
            <table className="tbl">
              <tbody>
                {stats.fixedFields.map((x) => (
                  <tr key={x.key}><td className="mono">{x.key}</td><td className="num">{fmt(x.n)} پرچم</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
